<!-- Tab bar -->
<div class="flex border-b border-slate-200 px-6" role="tablist">
  <button
    *ngFor="let tab of tabs"
    (click)="selectedTab = tab.id"
    role="tab"
    [attr.aria-selected]="selectedTab === tab.id"
    class="mr-6 border-b-2 py-3 text-sm font-medium transition-colors focus:outline-none"
    [class.border-violet-600]="selectedTab === tab.id"
    [class.text-violet-700]="selectedTab === tab.id"
    [class.border-transparent]="selectedTab !== tab.id"
    [class.text-slate-500]="selectedTab !== tab.id"
    [class.hover:text-slate-700]="selectedTab !== tab.id"
  >
    {{ tab.label }}
  </button>
</div>

<!-- ── Overview ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">

  <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-slate-400">State Machines</p>
      <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
      <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
    </div>
    <div class="rounded-lg border p-4"
         [class.border-red-200]="failedCount > 0"
         [class.bg-red-50]="failedCount > 0"
         [class.border-slate-200]="failedCount === 0"
         [class.bg-slate-50]="failedCount === 0">
      <p class="text-xs font-medium uppercase tracking-wide"
         [class.text-red-500]="failedCount > 0"
         [class.text-slate-400]="failedCount === 0">Last Run Failed / Timed Out</p>
      <p class="mt-1 text-2xl font-bold"
         [class.text-red-700]="failedCount > 0"
         [class.text-slate-900]="failedCount === 0">{{ failedCount }}</p>
    </div>
    <div class="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <p class="text-xs font-medium uppercase tracking-wide text-blue-500">Running</p>
      <p class="mt-1 text-2xl font-bold text-blue-700">{{ runningCount }}</p>
    </div>
  </div>

</div>

<!-- ── State Machines ─────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'state-machines'" role="tabpanel">

  <!-- Filter bar spans full width -->
  <app-cloud-filter-bar
    entityName="state machines"
    placeholder="Search name or ARN…"
    [facets]="facets"
    [sortOptions]="sortOptions"
    [filters]="filters"
    [resultCount]="filteredStateMachines.length"
    [totalCount]="stateMachines.length"
    [quotaUsage]="quotaUsage"
    (filtersChange)="onFiltersChange($event)"
  ></app-cloud-filter-bar>

  <div class="flex min-h-0" [class.divide-x]="selectedSm" [class.divide-slate-200]="selectedSm">

    <!-- Table panel -->
    <div class="min-w-0 flex-1 p-6">

      <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading state machines…
      </div>

      <div *ngIf="!loading" class="overflow-x-auto">
        <table class="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr class="border-b border-slate-200">
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Name</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Type</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Last Execution</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Last Run At</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Routing</th>
              <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let sm of filteredStateMachines"
                class="border-b border-slate-100 last:border-0"
                [class.bg-violet-50]="sm.arn === selectedSm">
              <td class="py-3 pr-4">
                <span class="font-mono text-xs font-medium text-slate-900">{{ sm.name }}</span>
                <p class="mt-0.5 truncate font-mono text-[10px] text-slate-400 max-w-xs">{{ sm.arn }}</p>
              </td>
              <td class="py-3 pr-4">
                <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      [class.bg-violet-50]="sm.type === 'STANDARD'"
                      [class.text-violet-700]="sm.type === 'STANDARD'"
                      [class.bg-sky-50]="sm.type === 'EXPRESS'"
                      [class.text-sky-700]="sm.type === 'EXPRESS'">
                  {{ sm.type }}
                </span>
              </td>
              <td class="py-3 pr-4">
                <span *ngIf="sm.lastExecutionStatus"
                      class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      [class]="executionStatusClass(sm.lastExecutionStatus)">
                  {{ executionStatusLabel(sm.lastExecutionStatus) }}
                </span>
                <span *ngIf="!sm.lastExecutionStatus" class="text-xs text-slate-400">—</span>
              </td>
              <td class="py-3 pr-4 text-xs text-slate-600">{{ sm.lastExecutionAt | cloudDate }}</td>
              <!-- Monitoring -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <ng-container *ngIf="isMonitored(sm.arn); else sfNotMonitored">
                  <button
                    type="button"
                    (click)="toggleMonitoring(sm.arn)"
                    class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    aria-label="Disable monitoring"
                  >
                    <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    Monitored
                  </button>
                </ng-container>
                <ng-template #sfNotMonitored>
                  <ng-container *ngIf="canMonitorMore; else sfQuotaFull">
                    <button
                      type="button"
                      (click)="toggleMonitoring(sm.arn)"
                      class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    >
                      Monitor
                    </button>
                  </ng-container>
                  <ng-template #sfQuotaFull>
                    <span class="group relative inline-block">
                      <button
                        type="button"
                        (click)="toggleMonitoring(sm.arn)"
                        aria-disabled="true"
                        class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none"
                      >
                        Monitor
                      </button>
                      <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        Quota reached
                      </span>
                    </span>
                  </ng-template>
                </ng-template>
              </td>
              <!-- Routing -->
              <td class="py-3 pr-4 whitespace-nowrap">
                <div class="flex items-center gap-1.5">
                  <span *ngIf="itemOverrideSummary(sm.arn)"
                        class="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-600">
                    {{ itemOverrideSummary(sm.arn) }}
                  </span>
                  <button
                    type="button"
                    (click)="openItemSettings(sm.arn)"
                    class="flex h-5 w-5 items-center justify-center rounded text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    [class.text-violet-500]="!!itemOverrideSummary(sm.arn)"
                    [attr.aria-label]="'Configure routing for ' + sm.name"
                  >
                    <lucide-icon name="settings" [size]="12" [strokeWidth]="2"></lucide-icon>
                  </button>
                </div>
              </td>
              <!-- View executions -->
              <td class="py-3 pr-2 whitespace-nowrap">
                <button
                  type="button"
                  (click)="sm.arn === selectedSm ? closeSm() : selectSm(sm.arn)"
                  class="rounded border px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.border-violet-300]="sm.arn === selectedSm"
                  [class.bg-violet-50]="sm.arn === selectedSm"
                  [class.text-violet-700]="sm.arn === selectedSm"
                  [class.border-slate-200]="sm.arn !== selectedSm"
                  [class.text-slate-500]="sm.arn !== selectedSm"
                  [class.hover:border-slate-300]="sm.arn !== selectedSm"
                  [class.hover:text-slate-700]="sm.arn !== selectedSm"
                >
                  {{ sm.arn === selectedSm ? 'Close' : 'View executions' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state: no matches -->
        <div *ngIf="filteredStateMachines.length === 0 && stateMachines.length > 0" class="py-10 text-center">
          <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="text-sm font-medium text-slate-500">No state machines match your filters</p>
          <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
        </div>

        <!-- Empty state: no data -->
        <p *ngIf="stateMachines.length === 0" class="py-6 text-sm text-slate-400">
          No Step Functions state machines found in this account and region.
        </p>
      </div>

    </div>

    <!-- Execution history panel -->
    <div *ngIf="selectedSm" class="flex w-96 shrink-0 flex-col">
      <!-- Header -->
      <div class="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <lucide-icon name="clock" [size]="15" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-semibold text-slate-700">Execution History</p>
          <p class="truncate font-mono text-[10px] text-slate-400">{{ selectedSm }}</p>
        </div>
        <button
          type="button"
          (click)="closeSm()"
          class="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
          aria-label="Close"
        >
          <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
        </button>
      </div>

      <!-- Loading -->
      <div *ngIf="smExecutionsLoading" class="flex items-center gap-2 px-4 py-6 text-sm text-slate-400">
        <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
        Loading executions…
      </div>

      <!-- Error -->
      <p *ngIf="smExecutionsError" class="px-4 py-4 text-xs text-red-500">{{ smExecutionsError }}</p>

      <!-- List -->
      <div *ngIf="!smExecutionsLoading && !smExecutionsError" class="flex-1 overflow-y-auto">
        <div *ngIf="smExecutions.length === 0" class="px-4 py-6 text-xs text-slate-400">No executions found.</div>
        <div *ngFor="let ex of pagedSmExecutions"
             class="border-b border-slate-100 px-4 py-3 last:border-0">
          <div class="flex items-start justify-between gap-2">
            <span class="min-w-0 truncate font-mono text-[11px] text-slate-700">{{ ex.name }}</span>
            <span class="inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  [class]="smExecutionStatusClass(ex.status)">{{ ex.status }}</span>
          </div>
          <p *ngIf="ex.startDate" class="mt-0.5 text-[10px] text-slate-400">Started: {{ ex.startDate | cloudDate }}</p>
          <p *ngIf="ex.stopDate"  class="text-[10px] text-slate-400">Ended: {{ ex.stopDate | cloudDate }}</p>
        </div>
      </div>

      <!-- Pagination -->
      <div *ngIf="smPageTotal > 1"
           class="flex items-center justify-between border-t border-slate-200 px-4 py-2">
        <button
          type="button"
          (click)="smPage = smPage - 1"
          [disabled]="smPage === 1"
          class="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100 disabled:opacity-40 focus:outline-none"
        >← Prev</button>
        <span class="text-[11px] text-slate-400">{{ smPage }} / {{ smPageTotal }}</span>
        <button
          type="button"
          (click)="smPage = smPage + 1"
          [disabled]="smPage === smPageTotal"
          class="rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100 disabled:opacity-40 focus:outline-none"
        >Next →</button>
      </div>
    </div>

  </div>

</div>

<!-- ── Activity ───────────────────────────────────────────────────── -->
<div *ngIf="selectedTab === 'activity'" role="tabpanel">
  <app-cloud-activity-feed
    [items]="activity"
    [loading]="activityLoading"
    serviceLabel="Step Functions"
    emptyHint="Recent executions and execution-failure findings appear here."
    (itemSelect)="onActivitySelect($event)"
  ></app-cloud-activity-feed>
</div>

<!-- ── Item settings drawer ──────────────────────────────────────── -->
<app-item-settings-drawer
  [service]="serviceCard"
  [itemId]="drawerItemId"
  [open]="drawerItemId !== null"
  (closed)="closeItemSettings()"
></app-item-settings-drawer>
